// Modified redictive search code from the revant dev theme

// Helper function to get the correct search API URL based on current domain
function getSearchApiUrl() {
  const hostname = window.location.hostname;
  const fullUrl = window.location.href;

  // We consider it production if it's the main site OR a preview from the prod Shopify admin.
  // A preview from the prod admin will have a referrer URL containing '/store/revantoptics'
  // but not '/store/revantoptics-dev'.
  const isProduction =
    hostname === "www.revantoptics.com" ||
    hostname === "revantoptics.com" ||
    fullUrl.includes("/store/revantoptics") ||
    fullUrl.includes("revantoptics.myshopify.com");

  if (isProduction) {
    return "https://enhanced-search-prod-lmkyt.ondigitalocean.app";
  }

  // For all other cases (like dev.revantoptics.com or previews from the dev admin),
  // use the development URL.
  return "https://revant-enhanced-search-fl5m2.ondigitalocean.app";
}

const searchApiUrl = getSearchApiUrl();

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g"))?.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  async getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    try {
      // Make both requests in parallel
      const [shopifyResponse, fuzzyResponse] = await Promise.all([
        fetch(
          `${routes.predictive_search_url}?q=${encodeURIComponent(
            searchTerm
          )}&section_id=predictive-search`,
          {
            signal: this.abortController.signal,
          }
        ),
        fetch(
          `${searchApiUrl}/api/search?q=${encodeURIComponent(searchTerm)}`,
          {
            signal: this.abortController.signal,
            headers: {
              Accept: "application/json",
            },
          }
        ),
      ]);

      const shopifyText = await shopifyResponse.text();
      const fuzzyProducts = await fuzzyResponse.json();

      // Parse the Shopify HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(shopifyText, "text/html");
      const resultsContainer = doc.querySelector(
        "#shopify-section-predictive-search"
      );

      // Find the products list
      const productsList = resultsContainer.querySelector(
        "#predictive-search-results-products-list"
      );

      if (productsList && fuzzyProducts.products.length > 0) {
        // Extract existing Shopify product handles
        const existingItems = Array.from(productsList.children);
        const shopifyProductHandles = new Set();

        // Extract handles from existing Shopify search results
        existingItems.forEach((item) => {
          const linkElement = item.querySelector("a");
          if (linkElement && linkElement.href) {
            const url = new URL(linkElement.href, window.location.origin);
            const pathSegments = url.pathname.split("/");
            // Get the product handle from URL path
            if (pathSegments.length >= 3 && pathSegments[1] === "products") {
              shopifyProductHandles.add(pathSegments[2]);
            }
          }
        });

        // Filter our fuzzy results to remove duplicates
        const uniqueFuzzyProducts = fuzzyProducts.products.filter(
          (product) => !shopifyProductHandles.has(product.handle)
        );

        console.log(
          `Found ${fuzzyProducts.products.length} fuzzy results, ${uniqueFuzzyProducts.length} unique`
        );

        // Create HTML for unique fuzzy results with proper IDs
        const fuzzyResultsHTML = uniqueFuzzyProducts
          .map((product, index) => {
            // Convert price values to numbers for proper comparison
            const priceMin = parseFloat(product.price_min) || 0;
            const priceMax = parseFloat(product.price_max) || 0;
            const price = parseFloat(product.price) || 0;
            const compareAtPrice = parseFloat(product.compare_at_price) || 0;

            return `
            <li id="predictive-search-option-product-${
              index + 1
            }" class="predictive-search__list-item" role="option" aria-selected="false">
              <a href="/products/${
                product.handle
              }" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
                <img class="predictive-search__image" loading="lazy" 
                     src="${product.image}&width=150" 
                     width="56" height="56.0" 
                     alt="${product.title}">
                <div class="predictive-search__item-content">
                  <p class="predictive-search__item-heading h5 product-heading">
                    ${
                      product.type === "Replacement Lenses"
                        ? `<span class="super-title">Replacement Lenses for</span>`
                        : ""
                    }
                    ${product.title.replace(
                      "Revant Replacement Lenses for ",
                      ""
                    )}
                  </p>
                  <div class="price ${
                    !product.available ? "price--sold-out" : ""
                  } ${compareAtPrice > price ? "price--on-sale" : ""}">
                    <div class="price__container">
                      <div class="price__regular">
                        <span class="visually-hidden visually-hidden--inline">Regular price</span>
                        <span class="price-item price-item--regular">
                          ${
                            priceMin < priceMax
                              ? '<span class="text--small subdued">From</span> '
                              : ""
                          }
                          $${product.price}
                        </span>
                        ${product.type === "Replacement Lenses" ? "/pair" : ""}
                      </div>
                    </div>
                  </div>
                </div>
              </a>
            </li>
          `;
          })
          .join("");

        // Update IDs for existing items to account for our fuzzy results
        const updatedExistingItemsHTML = existingItems
          .map((item, index) => {
            const clone = item.cloneNode(true);
            // Update the ID to follow after our fuzzy results
            clone.id = `predictive-search-option-product-${
              index + uniqueFuzzyProducts.length + 1
            }`;
            return clone.outerHTML;
          })
          .join("");

        // Combine fuzzy results with updated existing items - fuzzy results first
        productsList.innerHTML = fuzzyResultsHTML + updatedExistingItemsHTML;
      }

      const resultsMarkup = resultsContainer.innerHTML;

      // Cache and render
      this.allPredictiveSearchInstances.forEach((instance) => {
        instance.cachedResults[queryKey] = resultsMarkup;
      });
      this.renderSearchResults(resultsMarkup);
    } catch (error) {
      if (error?.code === 20) return; // Aborted
      this.close();
      throw error;
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector(".section-header")?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
  }
}

customElements.define("predictive-search", PredictiveSearch);

document.addEventListener("alpine:init", () => {
  Alpine.data("SearchProducts", () => ({
    cache: {},
    loaded: false,
    search_products: [],
    limit: 10,
    init() {
      this.InitCache();
      this.SearchProducts();
    },
    InitCache() {
      let am = this;
      am.cache.$form = am.$el.closest("form");
      am.cache.$input = am.cache.$form.querySelector(".search__input");
    },
    async SearchProducts() {
      let am = this;
      let searchTerm = am.cache.$input.value;
      // additional searches
      // 1. search for products only
      // 2. product with tag with prefix "frame-sku:"
      console.time("searching products");
      let results_search_products = await Promise.all([
        fetch(
          `${searchApiUrl}/api/search?q=${encodeURIComponent(searchTerm)}`,
          {
            headers: {
              Accept: "application/json",
            },
          }
        )
          .then((res) => res.json())
          .then(({ products }) => {
            console.log("products: ", products);
            return {
              resources: {
                results: {
                  products,
                },
              },
            };
          }),
        fetch(
          `${routes.predictive_search_url}.json?q=${encodeURIComponent(
            searchTerm
          )}&resources[type]=product`
        ).then((res) => res.json()),
        fetch(
          `${
            routes.predictive_search_url
          }.json?q=frame-sku:${encodeURIComponent(
            searchTerm
          )}&resources[type]=product&resources[options][fields]=tag`
        ).then((res) => res.json()),
      ]);

      console.timeEnd("searching products");

      let search_products = results_search_products
        .flatMap((item) => item.resources.results.products)
        .slice(0, am.limit);

      // remove duplicates to prevent same item on both queries
      let handles = [...new Set(search_products.map((item) => item.handle))];
      let products = [];
      handles.forEach((handle) => {
        products.push(search_products.find((item) => item.handle == handle));
      });
      am.loaded = true;
      am.search_products = products;
    },
  }));
});
